import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Buildings, CurrencyDollar, FileText, Handshake, ArrowRight } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1723107638858-331404b1a09a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYWVyaWFsfGVufDB8fHx8MTc4NTA0MTIxMXww&ixlib=rb-4.1.0&q=85";

const fmtMoney = (n) => "$" + (n || 0).toLocaleString("en-US");

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const kpis = [
    { label: "Active Projects", value: stats?.project_count ?? "—", icon: Buildings },
    { label: "Total Budget", value: stats ? fmtMoney(stats.total_budget) : "—", icon: CurrencyDollar },
    { label: "Documents", value: stats?.document_count ?? "—", icon: FileText },
    { label: "GC Bids", value: stats?.bid_count ?? "—", icon: Handshake },
  ];

  return (
    <div>
      <div className="relative h-48 lg:h-56 bg-[hsl(var(--blueprint))] overflow-hidden">
        <img src={HERO} alt="Construction site" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-6 lg:p-8 text-white">
          <div className="overline text-primary mb-2">Welcome back, {user?.username}</div>
          <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Project Command Center</h1>
        </div>
      </div>

      <div className="p-6 lg:p-8 -mt-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="bg-card border border-border p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-center justify-between mb-4">
                  <Icon size={22} weight="duotone" className="text-primary" />
                </div>
                <div className="font-heading font-black text-2xl lg:text-3xl tracking-tighter">{k.value}</div>
                <div className="overline text-muted-foreground mt-1">{k.label}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg tracking-tight">Your Projects</h2>
            <button onClick={() => navigate("/projects")} data-testid="view-all-projects"
              className="text-sm text-primary flex items-center gap-1 hover:gap-2 transition-all duration-150">
              View all <ArrowRight size={16} />
            </button>
          </div>

          {stats?.projects?.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.projects.map((p) => (
                <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                  data-testid={`dashboard-project-${p.id}`}
                  className="text-left bg-card border border-border p-5 hover:border-primary transition-colors duration-150 group">
                  <div className="overline text-primary mb-2">{p.brand}</div>
                  <div className="font-heading font-bold text-lg tracking-tight group-hover:text-primary transition-colors duration-150">{p.name}</div>
                  <div className="text-sm text-muted-foreground mt-1">{p.address || "No address set"}</div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <span className="text-xs px-2 py-1 bg-secondary">{p.status}</span>
                    <span className="font-heading font-bold text-sm">{fmtMoney(p.estimated_budget)}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border border-dashed p-12 text-center">
              <p className="text-muted-foreground mb-4">No projects yet. Set up your first hotel construction project.</p>
              <button onClick={() => navigate("/projects")} data-testid="create-first-project"
                className="bg-primary text-white px-5 py-2.5 rounded-none hover:-translate-y-[1px] transition-transform duration-150">
                Create Project
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
